/**
 * A document's text, read in the owner's browser. PDF and Word (.docx) only.
 *
 * WHY HERE AND NOT ON THE SERVER. The Worker runs on a plan that allows it ten milliseconds of
 * CPU a request, and parsing a PDF is not ten milliseconds. The browser has all the CPU it
 * needs, and the file never has to leave the owner's machine: only its text is sent.
 *
 * THE PARSERS ARE LOADED WHEN A FILE IS PICKED, not with the page. pdf.js and mammoth are most
 * of a megabyte between them, and nobody opening Settings to change their hours should download
 * either.
 */

export class ExtractError extends Error {}

export interface Extracted {
  text: string;
  /** A PDF's page count. A Word file has no fixed pages to count. */
  pages: number | null;
}

export const ACCEPT = ".pdf,.docx";

export async function extractText(file: File): Promise<Extracted> {
  const extension = file.name.toLowerCase().split(".").pop() ?? "";
  let result: Extracted;
  if (extension === "pdf") result = await pdfText(file);
  else if (extension === "docx") result = { text: await docxText(file), pages: null };
  else throw new ExtractError("Only PDF and Word (.docx) files can be added.");

  const clean = result.text.replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  if (!clean) {
    throw new ExtractError(
      extension === "pdf"
        ? "This PDF has no text in it - it may be a scan. Add the text another way."
        : "This file has no text in it.",
    );
  }
  return { ...result, text: clean };
}

async function pdfText(file: File): Promise<Extracted> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  let doc;
  try {
    doc = await task.promise;
  } catch {
    throw new ExtractError("This PDF could not be opened. It may be protected with a password.");
  }
  const pages: string[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const content = await (await doc.getPage(n)).getTextContent();
    let page = "";
    for (const item of content.items) {
      if (!("str" in item)) continue;
      page += item.str + (item.hasEOL ? "\n" : " ");
    }
    pages.push(page.trim());
  }
  const count = doc.numPages;
  // The loading task owns the worker; destroying it frees the document with it.
  await task.destroy();
  return { text: pages.join("\n\n"), pages: count };
}

async function docxText(file: File): Promise<string> {
  const mammoth = await import("mammoth");
  try {
    const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
    return result.value;
  } catch {
    throw new ExtractError("This Word file could not be opened.");
  }
}
